import { apiRequest, apiRequestSSE, ApiError } from '@/shared/api';

export type RemarkStatus = 'new' | 'in_progress' | 'fixed' | 'verified' | 'rejected';
export type RemarkSeverity = 'low' | 'medium' | 'high';
export type RemarkCategory =
  'reply_quality' | 'grounding' | 'journey' | 'tone' | 'tooling' | 'knowledge' | 'other';

export const REMARK_STATUSES: RemarkStatus[] = [
  'new',
  'in_progress',
  'fixed',
  'verified',
  'rejected',
];

export interface Remark {
  id: string;
  dialog_id: string;
  message_id?: string;
  run_id?: string;
  title: string;
  body: string;
  category: RemarkCategory | string;
  severity: RemarkSeverity | string;
  status: RemarkStatus | string;
  created_by_user_id?: string;
  resolution?: string;
  commit_ref?: string;
  created_at: string;
  updated_at: string;
}

export interface RemarkEvent {
  id: string;
  remark_id: string;
  actor: string;
  kind: 'created' | 'status_changed' | 'note' | 'commit' | string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface ReviewMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface ProposedRemark {
  title: string;
  body: string;
  category: string;
  severity: string;
  message_id?: string;
  run_id?: string;
}

export interface ReviewChatResult {
  reply: string;
  user_message: ReviewMessage;
  assistant_message: ReviewMessage;
  pending_remarks?: ProposedRemark[];
  created_remarks?: Remark[];
}

export interface PlaygroundSessionSummary {
  id: string;
  created_at: string;
  last_activity_at: string;
  first_message: string;
  message_count: number;
  remarks_total: number;
  remarks_open: number;
}

export async function listRemarks(params?: {
  status?: string;
  dialog_id?: string;
  limit?: number;
}): Promise<Remark[]> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.dialog_id) query.set('dialog_id', params.dialog_id);
  if (params?.limit) query.set('limit', String(params.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  const data = await apiRequest<{ remarks: Remark[] }>(`/remarks${suffix}`);
  return data.remarks ?? [];
}

export async function getRemark(id: string): Promise<Remark> {
  return apiRequest<Remark>(`/remarks/${encodeURIComponent(id)}`);
}

export async function listRemarkEvents(id: string): Promise<RemarkEvent[]> {
  const data = await apiRequest<{ events: RemarkEvent[] }>(
    `/remarks/${encodeURIComponent(id)}/events`,
  );
  return data.events ?? [];
}

export async function updateRemark(
  id: string,
  input: { status?: string; resolution?: string; commit_ref?: string; note?: string },
): Promise<Remark> {
  return apiRequest<Remark>(`/remarks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function listPlaygroundSessions(limit = 50): Promise<PlaygroundSessionSummary[]> {
  const data = await apiRequest<{ sessions: PlaygroundSessionSummary[] }>(
    `/playground/sessions?limit=${limit}`,
  );
  return data.sessions ?? [];
}

export type ReviewScope = 'playground' | 'chats';

function reviewRoot(scope?: ReviewScope): string {
  return scope === 'chats' ? '/chats' : '/playground/sessions';
}

export async function getDialogReview(
  dialogId: string,
  scope?: ReviewScope,
): Promise<{ messages: ReviewMessage[]; remarks: Remark[] }> {
  const data = await apiRequest<{ messages: ReviewMessage[]; remarks: Remark[] }>(
    `${reviewRoot(scope)}/${encodeURIComponent(dialogId)}/review`,
  );
  return { messages: data.messages ?? [], remarks: data.remarks ?? [] };
}

export async function sendReviewChat(
  dialogId: string,
  input: { content: string; allow_mutations?: boolean; confirm_remarks?: ProposedRemark[] },
  options?: { signal?: AbortSignal; scope?: ReviewScope },
): Promise<ReviewChatResult> {
  let result: ReviewChatResult | null = null;
  let streamError: string | null = null;

  await apiRequestSSE(`${reviewRoot(options?.scope)}/${encodeURIComponent(dialogId)}/review/chat`, {
    method: 'POST',
    body: {
      content: input.content,
      allow_mutations: Boolean(input.allow_mutations),
      confirm_remarks: input.confirm_remarks,
    },
    signal: options?.signal,
    onEvent: (event, data) => {
      if (event === 'result' && data && typeof data === 'object') {
        result = data as ReviewChatResult;
        return;
      }
      if (event === 'error') {
        if (data && typeof data === 'object' && 'error' in data) {
          const message = (data as { error: unknown }).error;
          streamError = typeof message === 'string' ? message : 'Review chat failed';
        } else {
          streamError = 'Review chat failed';
        }
      }
    },
  });

  if (streamError) {
    throw new ApiError(streamError, 500);
  }
  if (!result) {
    throw new ApiError('Review chat ended without a result', 500);
  }
  return result;
}
